import { memo, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Redirect } from 'react-router-dom'
import { useSchedulingContext } from 'context/SchedulingContext/SchedulingProvider'
import useAppointmentTypeId from 'hooks/useAppointments/useAppointmentTypeId'
import useAppointments from 'hooks/useAppointments/useAppointments'
import { RoutePaths } from 'containers/Core/Routes'
import useUser from 'hooks/useUser/useUser'
import AppointmentNotBookableMessage from './AppointmentBanner'
import LoadingState from 'components/Spinner'
import AppointmentOption from './AppointmentOption'
import { Button } from '@chakra-ui/react'

type AppointmentInfo = {
  appointmentTypeId: string | undefined
  appointmentText: string
}

const AppointmentTypeScreen: React.FC = () => {
  const { nextScreen, setAppointmentTypeId } = useSchedulingContext()
  const [selectedAppointmentTypeId, setSelectedAppointmentTypeId] = useState<
    string | undefined
  >('')
  const {
    appointmentTypeId: followupRdAppointmentTypeId,
  } = useAppointmentTypeId('Followup RD')

  const { user } = useUser()

  const { appointmentEligibilityData } = useAppointments()

  const { apptTypeIds } = appointmentEligibilityData

  if (!apptTypeIds) {
    return <Redirect to={RoutePaths.DASHBOARD} />
  }

  const { medicalApptTypeId, nutritionalApptTypeId } = apptTypeIds

  // md/rd appointment types will be undefined if patient is not eligible to book either
  const hasNoEligibleAppointmentTypes =
    !medicalApptTypeId && !nutritionalApptTypeId

  const handleSubmit = () => {
    setAppointmentTypeId(selectedAppointmentTypeId)
    nextScreen()
  }

  const appointmentTypes: AppointmentInfo[] =
    user?.stripeCustomer?.product.type === 'nutrition-only'
      ? [
          {
            appointmentTypeId: nutritionalApptTypeId,
            appointmentText: 'Nutrition Visit',
          },
        ]
      : [
          {
            appointmentTypeId: medicalApptTypeId,
            appointmentText: 'Medical Visit',
          },
          {
            appointmentTypeId: nutritionalApptTypeId,
            appointmentText: 'Nutrition Visit',
          },
        ]

  // md/rd appointment types will be both defined if patient can book both
  const hasEligibleAppointmentTypes = medicalApptTypeId && nutritionalApptTypeId

  // if feature flag is enabled + md/rd appointment types are undefined
  // patient is not eligible to book either kind of appointment
  const isPatientNotEligibleForSchedulingAppts = hasNoEligibleAppointmentTypes
  const isSubmitDisabled =
    !selectedAppointmentTypeId || isPatientNotEligibleForSchedulingAppts

  // hide appointment banner with upcoming appointments + modify CTAs
  // if patient is able to book both kinds of appointments or
  // if patient is nutrition only, able to book a follow up rd
  const isAppointmentBannerHidden =
    hasEligibleAppointmentTypes ||
    (user?.stripeCustomer?.product.type === 'nutrition-only' &&
      nutritionalApptTypeId === followupRdAppointmentTypeId)

  const submitButton = (
    <Button
      type="submit"
      width="100%"
      mb={0}
      mt={5}
      backgroundColor="var(--blue-dark)"
      color="white"
      _hover={{
        backgroundColor: 'var(--blue-dark-hover)',
        color: 'white',
      }}
      isDisabled={isSubmitDisabled}
      onClick={() => handleSubmit()}
    >
      Next
    </Button>
  )

  return (
    <div className="screen-container">
      {isAppointmentBannerHidden ? null : (
        <AppointmentNotBookableMessage
          medicalApptTypeId={medicalApptTypeId}
          nutritionalApptTypeId={nutritionalApptTypeId}
          isFollowUpRdTypeId={
            nutritionalApptTypeId === followupRdAppointmentTypeId
          }
          program={user?.stripeCustomer?.product.type}
        />
      )}
      <h5
        className={`ff-inter-medium fs-8
          ${
            isPatientNotEligibleForSchedulingAppts
              ? 'appointment-type-header-disabled'
              : 'appointment-type-header'
          }
          `}
      >
        Please select your visit type
      </h5>
      <div className="d-flex flex-column flex-sm-row mb-3 gap-2 align-items-center">
        {appointmentTypes.map((appointmentType) => {
          const apptTypeId = appointmentType.appointmentTypeId

          const isSelected = selectedAppointmentTypeId === apptTypeId

          return (
            <AppointmentOption
              key={appointmentType.appointmentText}
              appointmentTypeId={apptTypeId}
              appointmentText={appointmentType.appointmentText}
              isSelected={isSelected}
              isDisabled={!apptTypeId}
              handleSelect={() => setSelectedAppointmentTypeId(apptTypeId)}
            />
          )
        })}
      </div>

      <div className="submit-button-container">
        {isSubmitDisabled ? (
          isPatientNotEligibleForSchedulingAppts ? (
            <span>{submitButton}</span>
          ) : (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="submit-button-tool-tip">
                  Please select a visit type
                </Tooltip>
              }
            >
              <span>{submitButton}</span>
            </OverlayTrigger>
          )
        ) : null}

        {!isSubmitDisabled && submitButton}
      </div>
    </div>
  )
}

export default memo(AppointmentTypeScreen)
